import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString
} from 'libphonenumber-js/max';
import { normalizePhoneNumber } from '../utils/phoneNumber';

type Props = React.InputHTMLAttributes<HTMLInputElement>;
type CountrySelection = CountryCode | '';

function splitPhone(value: string, defaultCountry: CountrySelection) {
  const phone = parsePhoneNumberFromString(value.trim().replace(/^00/, '+'), {
    defaultCountry: 'SE',
    extract: false
  });
  return {
    country: phone?.country ?? defaultCountry,
    national: phone?.country ? (phone.nationalNumber as string) : value
  };
}

/** A country selector and a national number; parents receive one E.164 value. */
export function PhoneInput({
  value = '',
  onChange,
  onBlur,
  className = '',
  id,
  name,
  disabled,
  readOnly,
  ...props
}: Props) {
  const { t, i18n } = useTranslation();
  const language = i18n?.resolvedLanguage || i18n?.language || 'sv';
  const defaultCountry: CountrySelection = language.startsWith('sv') ? 'SE' : '';
  const generatedId = useId();
  const inputId = id || name || generatedId;
  const externalValue = String(value);
  const [parts, setParts] = useState(() => splitPhone(externalValue, defaultCountry));
  const countryChosen = useRef(Boolean(externalValue.trim()));
  const [touched, setTouched] = useState(false);
  const emitted = useRef(externalValue);
  const input = useRef<HTMLInputElement>(null);
  const invalid = Boolean(externalValue.trim() && !normalizePhoneNumber(externalValue));
  const message = t('common.phone.invalid');
  const countries = useMemo(() => {
    const locale = i18n?.resolvedLanguage || i18n?.language || 'sv';
    const names = new Intl.DisplayNames([locale], { type: 'region' });
    return getCountries()
      .map((country) => ({ country, label: names.of(country) || country }))
      .sort((a, b) =>
        a.country === 'SE' ? -1 : b.country === 'SE' ? 1 : a.label.localeCompare(b.label, locale)
      );
  }, [i18n?.language, i18n?.resolvedLanguage]);

  useEffect(() => {
    if (externalValue !== emitted.current) {
      setParts(splitPhone(externalValue, defaultCountry));
      countryChosen.current = Boolean(externalValue.trim());
      emitted.current = externalValue;
      setTouched(false);
    } else if (!countryChosen.current) {
      setParts((current) =>
        current.country === defaultCountry ? current : { ...current, country: defaultCountry }
      );
    }
  }, [externalValue, defaultCountry]);

  useEffect(() => {
    input.current?.setCustomValidity(invalid && !disabled && !readOnly ? message : '');
  }, [invalid, disabled, readOnly, message]);

  const change = (
    national: string,
    country: CountryCode,
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const international = /^(\+|00)/.test(national.trim());
    const parsed = international ? splitPhone(national, country) : { national, country };
    const nextCountry = international ? parsed.country : country;
    const normalized = normalizePhoneNumber(national, country);
    // Keep incomplete/invalid input editable, but never mistake it for a valid number.
    const nextValue = !national.trim()
      ? ''
      : (normalized ??
        (international
          ? national.trim().replace(/^00/, '+')
          : `+${getCountryCallingCode(country)}${national}`));
    setParts(international && normalized ? parsed : { national, country: nextCountry });
    countryChosen.current = true;
    emitted.current = nextValue;
    onChange?.({
      ...event,
      target: { ...event.target, name: name || '', value: nextValue },
      currentTarget: { ...event.target, name: name || '', value: nextValue }
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const describedBy =
    [props['aria-describedby'], touched && invalid ? `${inputId}-phone-error` : '']
      .filter(Boolean)
      .join(' ') || undefined;
  return (
    <div className="phone-input">
      <div className="row g-2">
        <div className="col-12 col-sm-5">
          <label className="visually-hidden" htmlFor={`${inputId}-country`}>
            {t('common.phone.countryCode')}
          </label>
          <select
            id={`${inputId}-country`}
            className="form-select phone-input-control"
            value={parts.country}
            disabled={disabled || readOnly}
            required={props.required}
            autoComplete="tel-country-code"
            onChange={(event) => change(parts.national, event.target.value as CountryCode, event)}
          >
            <option value="" disabled>
              {t('common.phone.selectCountry')}
            </option>
            {countries.map(({ country, label }) => (
              <option key={country} value={country}>
                {t('common.phone.countryOption', {
                  country: label,
                  code: getCountryCallingCode(country)
                })}
              </option>
            ))}
          </select>
        </div>
        <div className="col-12 col-sm-7">
          <input
            {...props}
            id={inputId}
            name={name}
            ref={input}
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            disabled={disabled || !parts.country}
            readOnly={readOnly}
            value={parts.national}
            className={`form-control phone-input-control ${className} ${touched && invalid ? 'is-invalid' : ''}`}
            aria-label={props['aria-label'] || t('common.phone.number')}
            aria-invalid={invalid && touched ? true : undefined}
            aria-describedby={describedBy}
            onChange={(event) => {
              if (parts.country) change(event.target.value, parts.country, event);
            }}
            onBlur={(event) => {
              setTouched(true);
              onBlur?.(event);
            }}
            onInvalid={() => setTouched(true)}
          />
        </div>
      </div>
      {touched && invalid && (
        <div id={`${inputId}-phone-error`} className="invalid-feedback d-block" role="alert">
          {message}
        </div>
      )}
    </div>
  );
}
